import { debounce } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';

import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useDraftMessages } from '@web/conversations/DraftMessagesProvider/DraftMessagesProvider';

import {
  DraftMessage,
  DraftMessagesProviderState,
  DraftMessagesCache,
} from './draftMessages.types';

const EMPTY_MESSAGE: DraftMessage = { text: '' };

export const useDraftMessagesContext = (cache?: DraftMessagesCache): DraftMessagesProviderState => {
  const draftMessages = useRef(cache?.getData() || {}).current;

  const getDraftMessage = useCallback(
    (convId: number) => {
      return draftMessages[convId] || EMPTY_MESSAGE;
    },
    [draftMessages]
  );

  const setDraftMessage = useCallback(
    (convId: number, message: string | DraftMessage) => {
      const messageToCache = typeof message === 'string' ? { text: message } : message;
      draftMessages[convId] = messageToCache;
      cache?.setData(draftMessages);
    },
    [cache, draftMessages]
  );

  const clearDraftMessage = useCallback(
    (convId: number) => {
      delete draftMessages[convId];
      cache?.setData(draftMessages);
    },
    [cache, draftMessages]
  );

  return {
    getDraftMessage,
    setDraftMessage,
    clearDraftMessage,
  };
};

export const useConversationDraftMessage = () => {
  const { id } = useConversationState();
  const { getDraftMessage, setDraftMessage, clearDraftMessage } = useDraftMessages();

  const draftMessage = useMemo(() => getDraftMessage(id), [getDraftMessage, id]);

  const onChange = useCallback(
    (draft: DraftMessage) => {
      const text = draft.text?.trim() || '';

      if (text) {
        setDraftMessage(id, text);
      } else {
        clearDraftMessage(id);
      }
    },
    [clearDraftMessage, id, setDraftMessage]
  );

  const onDraftMessageChange = useMemo(() => debounce(onChange, 200), [onChange]);

  return {
    draftMessage,
    onDraftMessageChange,
  };
};
